import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { RecoveryManifestDialog } from './RecoveryManifestDialog'
import { useCan } from '@/hooks/useCan'
import { recoverySetsApi, type RequestObjectDetailRow } from '../api'
import { RecoverySetObjectsTable } from './RecoverySetObjectsTable'

export function RecoverySetAllObjectsDialog({
    open,
    onOpenChange,
    setId,
    setName,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
    setId: number
    setName?: string | null
}) {
    const { t } = useTranslation()
    const { data, isLoading, isError } = useQuery({
        queryKey: ['recovery-set', 'set-objects', setId],
        queryFn: async () => {
            const res = await recoverySetsApi.getSetObjectDetails(setId)
            const payload = (res.data as { data?: RequestObjectDetailRow[] })?.data
            return payload ?? []
        },
        enabled: open && setId > 0,
    })
    const rows = data ?? []
    const title = setName ? `${t('recoverRequests.allObjectsInSet', 'All objects in recovery set')}: ${setName}` : t('recoverRequests.allObjectsInSet', 'All objects in recovery set')

    const [manifestOpen, setManifestOpen] = useState(false)
    // Building the set's manifest releases recovery data: recovery.data.
    const canRelease = useCan('recovery.data')

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[min(95vw,1120px)] max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0" aria-describedby="recovery-set-all-objects-desc">
                <DialogHeader className="px-6 pt-6 pb-2 text-left">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription id="recovery-set-all-objects-desc">
                                {t('recoverRequests.everyObjectInSet', 'Every object in this recovery set, across all request IDs.')}
                            </DialogDescription>
                        </div>
                        {/* One link for the whole set — what the engineer pastes into the PXE
                            recovery server console. */}
                        {canRelease && (
                            <Button size="sm" variant="outline" className="shrink-0" onClick={() => setManifestOpen(true)}>
                                <Link2 className="mr-2 h-4 w-4" />
                                {t('recoverRequests.recoveryManifest', 'Recovery manifest')}
                            </Button>
                        )}
                    </div>
                </DialogHeader>
                <div className="mx-6 mb-6 min-w-0 flex-1 min-h-0 overflow-y-auto">
                    <RecoverySetObjectsTable rows={rows} isLoading={isLoading} isError={isError} />
                </div>
            </DialogContent>
            <RecoveryManifestDialog
                open={manifestOpen}
                onOpenChange={setManifestOpen}
                recoverySet={setId}
            />
        </Dialog>
    )
}
